'use client';

import { useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';
import { useSearchParams } from 'next/navigation';

import Building from '@/icons/duotone/building.svg';
import { CardFlat, Grid, NotFound } from '@/molecules';

import { cardTransformer } from '@/graphql/transformers';
import { capitalize } from '@/utils';
import { gtmPush } from '@/utils/gtm';

import type { FunctionComponent } from 'react';
import type { TypeGridCardHitsProps } from './types';

export const GridCardHits: FunctionComponent<TypeGridCardHitsProps> = ({
  className = '',
  forceOneCol = false,
  redesign = false,
}: TypeGridCardHitsProps) => {
  const search = useSearchParams();
  const { hits, results } = useHits();
  const [currentPage, setCurrentPage] = useState<null | number>(null);
  const [isFilteringDates, setIsFilteringDates] = useState(false);
  const hierarchicalZonasKeys = Object.keys(
    results?._state.hierarchicalFacetsRefinements ?? {},
  );

  useEffect(() => {
    // TODO: tipar correctamente

    // @ts-ignore
    const { startDate, endDate } = search;
    setIsFilteringDates(startDate !== undefined || endDate !== undefined);

    if (
      typeof window !== 'undefined' &&
      hits &&
      results &&
      (results.page !== currentPage || isFilteringDates)
    ) {
      const listName =
        results?._state.hierarchicalFacetsRefinements[
          hierarchicalZonasKeys[0]
        ][0]?.toLowerCase();

      gtmPush({
        event: 'view_item_list',
        item_list_id: window.location.pathname,
        item_list_name: listName ?? 'Todos los pisos',
        page_number: results?.page,
        total_page_numbers: results?.nbPages,
        search_term: search,
        item_type: capitalize(hits?.[0]?.ventaAlquiler as string),
        items: hits?.map((hit: any) => ({
          item_id: hit?.codigoUnidadAlquiler,
          item_name: hit?.nombreUE,
          currency: 'EUR',
          start_date: startDate ? startDate : undefined,
          end_date: endDate ? endDate : undefined,
          discount: undefined, // TODO: añadir descuento cuando venga
          item_list_id: window.location.pathname,
          item_list_name: listName ?? 'Todos los pisos',
          price: parseFloat(
            hit.ventaAlquiler?.toLowerCase() === 'venta'
              ? hit.importeVenta
              : hit.importeRecibofinal,
          ),
        })),
      });

      setIsFilteringDates(false);
      setCurrentPage(results?.page);
    }
  }, [results, hits]);

  if (hits.length == 0) {
    return (
      <NotFound
        className={`mx-auto ${className}`}
        text="No tenemos viviendas en esta ubicación"
        subtext="Cambia o elimina algunos de los filtros o modifica el área de búsqueda."
        Icon={Building}
        surfaceClass={redesign ? 'bg-accent' : 'bg-primary-50'}
        // El icono duotone tiñe su edificio derecho con `primary-900`, que en
        // HoM es casi blanco y desaparece sobre el fondo `accent`. Se
        // recolorean los DOS edificios a la pareja del logo (cian + teal) para
        // que el icono lea como de marca, no como un cian suelto. Valores
        // arbitrarios con la CSS var para no depender de la utilidad `fill-*`.
        iconClassName={
          redesign
            ? '[&_path:first-child]:fill-[rgb(var(--tw-color-primary-400))] [&_path:last-child]:fill-[rgb(var(--tw-color-primary-600))]'
            : ''
        }
      />
    );
  }

  // Conservamos el Grid histórico para no romper la hidratación SSR de Algolia.
  const colOverride = forceOneCol
    ? 'grid-cols-1! sm:grid-cols-1! lg:grid-cols-1! xl:grid-cols-1!'
    : 'xl:grid-cols-[repeat(var(--listing-grid-cols),minmax(0,1fr))]!';

  return (
    <Grid
      xxs={1}
      sm={2}
      lg={1}
      xl={2}
      className={`gap-6 ${colOverride} ${className}`}
    >
      {hits.map((hit: any, key: number) => (
        <Grid.Item colSpanSM={1} key={hit?.id ?? key}>
          <CardFlat
            {...cardTransformer(
              hit,

              () => {},
            )}
            oneCol={forceOneCol}
          />
        </Grid.Item>
      ))}
    </Grid>
  );
};

GridCardHits.displayName = 'GridCardHits';
